/**
 * Générateur — Automatisme : écriture décimale des fractions simples
 * (1/2 ; 1/4 ; 3/4 ; 3/2 ; 4/2 ; 5/2 ; 1/10 ; 100/100 ; 7/1).
 * Attendu ID : 1048 | Niveau : 5ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME n°10 (programme 2025), « entretenir ». La liste est
 * celle de 6e (les six faits), plus ce que la 5e ajoute : 1/10, 100/100 et
 * 7/1, c'est-à-dire les dixièmes par la numération et les fractions ÉGALES À
 * UN ENTIER (sens quotient, qui prépare les dénominateurs quelconques).
 * Labels (cartes exerciseur) — deux cartes (cadrage prof 2026-09-07) :
 *   - Fractions usuelles et écritures décimales (1/4 = 0,25...)
 *       → DÉLÉGUÉE à gen_1012 (6e), re-badgée 5e : mêmes cinq formats,
 *         mêmes invariants, même coup de pouce, mêmes solveurs (alias).
 *   - Fractions égales à un entier, dixièmes et centièmes
 *       → propre à la 5e (trous saisie, QCM, Vrai/Faux).
 *
 * ── Carte 2, domaine PAR CONSTRUCTION (tout en millièmes entiers) ─────────
 *   - entier : n/1 (n 2..9), 2n/2 (n 3..9), 4n/4 (n 1..5), 10n/10 (n 1..9),
 *     k/k (k dans 3..9, 10, 100), n·100/100 (n 2..5) ;
 *   - dixiemes : n/10 (n 1..9), n/100 (n 1..9 → 0,0n ; n 11..99 hors
 *     multiples de 10, minoritaire), 1/1000 par héritage, minoritaire ;
 *   - moitie : n/2, n impair de 7 à 15 (3/2 et 5/2 sont dans la carte 1).
 *   Hors domaine, par construction : tiers, cinquièmes, quarts au-delà de
 *   3/4, dénominateurs quelconques, tout ce qui demande de poser une
 *   division. Chaque égalité émise est re-vérifiée (throw).
 *
 * DISTRACTEURS (QCM et V/F), familles nommées (cadrage prof) :
 *   - lu_tel_quel : les chiffres lus tels quels (1/10 → 1,10 ; 7/2 → 7,2 ;
 *     7/1 → 7,1) — LE piège, il trahit l'absence de sens ;
 *   - rang : erreur de rang (1/100 → 0,1 ; 3/10 → 0,03) ;
 *   - entier_lu_dixieme : la fraction égale à 1 ou à un entier lue comme un
 *     dixième (100/100 → 0,1 ; 7/1 → 0,7) ou comme le numérateur (100) ;
 *   - moitie_entiere : la moitié arrondie (7/2 → 3 ou 4) ;
 *   - vers fraction : fraction_inversee (0,3 → 10/3), rang (0,3 → 3/100).
 *   Tous re-vérifiés différents de la clé en VALEUR ; collision → nouveau
 *   tirage complet.
 *
 * COUP DE POUCE carte 2 : le sens quotient en trois blocs (division, moitié,
 * rang), exemples HORS domaine (12/1, 12/12, 120/10, 17/2, 3/10, 12/100) ;
 * 3 est exclu des numérateurs de n/10 et 12 de ceux de n/100. Invariant
 * protégé par cdp1048.self.test.js. Carte 1 : le coup de pouce de 1012.
 */
import { generate as genererUsuelles, CATEGORIES as CAT_USUELLES } from '../../6eme/nombres-decimaux/gen_1012';
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1048;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ENTIERS = 'Fractions égales à un entier, dixièmes et centièmes';

// ─── Carte 1 : délégation à gen_1012 ──────────────────────────────────────

const rebadger = (payload) => {
    payload.niveau = '5eme';
    payload.attendu = ATTENDU_ID;
    return payload;
};

// ─── Carte 2 : le domaine (millièmes entiers) ─────────────────────────────

/** Numérateurs exclus de n/10 et de n/100 : les exemples du coup de pouce
 *  (3/10 = 0,3 et 12/100 = 0,12), pour qu'aucune carte ne les affiche. */
export const NUMERATEURS_SACRIFIES_DIXIEMES = [3];
export const NUMERATEURS_SACRIFIES_CENTIEMES = [12];

/** millièmes → écriture française réduite (« 0,35 », « 3,5 », « 7 »). */
const fmt = (m) => {
    const ent = Math.floor(m / 1000);
    const r = String(m % 1000).padStart(3, '0').replace(/0+$/, '');
    return r === '' ? String(ent) : `${ent},${r}`;
};

const parseMil = (s) => {
    const m = String(s).match(/^(\d+)(?:,(\d{1,3}))?$/);
    if (!m) return null;
    return parseInt(m[1], 10) * 1000 + parseInt(((m[2] || '') + '000').slice(0, 3), 10);
};

/** Une fraction n/d de valeur décimale exacte en millièmes (assert). */
const verifierFraction = (n, d, famille) => {
    if (!Number.isInteger(n) || !Number.isInteger(d) || n <= 0 || d <= 0 || (n * 1000) % d !== 0) {
        throw new Error(`gen_1048 : fraction hors domaine ${n}/${d}`);
    }
    const mil = (n * 1000) / d;
    if (parseMil(fmt(mil)) !== mil) throw new Error(`gen_1048 : écriture non re-parsable ${n}/${d}`);
    if ((d === 100 && NUMERATEURS_SACRIFIES_CENTIEMES.includes(n)) || (d === 10 && NUMERATEURS_SACRIFIES_DIXIEMES.includes(n))) {
        throw new Error('gen_1048 : numérateur sacrifié');
    }
    return { n, d, mil, dec: fmt(mil), famille };
};

const tirerEntier = () => {
    // k/k une fois sur trois, et 100/100 (cité par la fiche) une fois sur
    // trois parmi les k/k (revue prof 2026-09-07 : mesuré à 0,7 % avant).
    if (randInt(1, 3) === 1) {
        const k = randInt(1, 3) === 1 ? 100 : randomChoice([3, 4, 5, 6, 7, 8, 9, 10]);
        return verifierFraction(k, k, 'entier');
    }
    const type = randomChoice(['sur_un', 'sur_deux', 'sur_quatre', 'sur_dix', 'sur_cent']);
    if (type === 'sur_un') return verifierFraction(randInt(2, 9), 1, 'entier');
    if (type === 'sur_deux') { const n = randInt(3, 9); return verifierFraction(2 * n, 2, 'entier'); }
    if (type === 'sur_quatre') { const n = randInt(1, 5); return verifierFraction(4 * n, 4, 'entier'); }
    if (type === 'sur_dix') { const n = randInt(1, 9); return verifierFraction(10 * n, 10, 'entier'); }
    return verifierFraction(100 * randInt(2, 5), 100, 'entier');
};

const tirerDixiemes = () => {
    const type = randomChoice(['dixieme', 'dixieme', 'centieme', 'centieme_deux', 'millieme']);
    if (type === 'dixieme') return verifierFraction(randomChoice([1, 2, 4, 5, 6, 7, 8, 9].filter((n) => !NUMERATEURS_SACRIFIES_DIXIEMES.includes(n))), 10, 'dixiemes');
    if (type === 'centieme') return verifierFraction(randInt(1, 9), 100, 'dixiemes');
    if (type === 'millieme') return verifierFraction(1, 1000, 'dixiemes');
    for (let essai = 0; essai < 40; essai++) {
        const n = randInt(11, 99);
        if (n % 10 === 0 || NUMERATEURS_SACRIFIES_CENTIEMES.includes(n)) continue;
        return verifierFraction(n, 100, 'dixiemes');
    }
    return verifierFraction(35, 100, 'dixiemes');
};

const tirerMoitie = () => verifierFraction(randomChoice([7, 9, 11, 13, 15]), 2, 'moitie');

const TIRAGES = { entier: tirerEntier, dixiemes: tirerDixiemes, moitie: tirerMoitie };
const tirerFraction = (famille) => TIRAGES[famille || randomChoice(['entier', 'dixiemes', 'moitie'])]();

const cleFraction = (f) => `${f.n}/${f.d}`;

// ─── Écritures ────────────────────────────────────────────────────────────

const fracTex = (f) => `\\dfrac{${f.n}}{${f.d}}`;
const decTex = (s) => String(s).replace(',', '{,}');

/** Une ligne KaTeX à un trou : décimal, numérateur ou dénominateur. */
const ligneTrou = (f, trou, sens) => {
    let gauche = fracTex(f);
    let droite = decTex(f.dec);
    if (trou === 'dec') droite = `{{${f.dec}}}`;
    else if (trou === 'num') gauche = `\\dfrac{{{${f.n}}}}{${f.d}}`;
    else gauche = `\\dfrac{${f.n}}{{{${f.d}}}}`;
    return '$' + (sens === 0 ? `${gauche} = ${droite}` : `${droite} = ${gauche}`) + '$';
};

// ─── Distracteurs ─────────────────────────────────────────────────────────

/** Vers le décimal : deux distracteurs en millièmes, familles nommées. */
const distracteursDecimal = (f) => {
    const cands = [];
    const push = (mil, code) => { if (mil !== null && mil > 0 && mil !== f.mil && !cands.some((c) => c.mil === mil)) cands.push({ mil, code }); };
    // lu tel quel : « n,d » (7/2 → 7,2 ; 1/10 → 1,10 ≡ 1,1 ; 7/1 → 7,1)
    if (f.d < 100) push(parseMil(`${f.n},${f.d}`), 'lu_tel_quel');
    if (f.famille === 'dixiemes') {
        push(f.mil * 10, 'rang');
        push(f.mil / 10 >= 1 && f.mil % 10 === 0 ? f.mil / 10 : null, 'rang');
    } else if (f.famille === 'entier') {
        push(f.mil / 10, 'entier_lu_dixieme');
        if (f.d > 1) push(f.n * 1000, 'entier_lu_dixieme'); // le numérateur (100/100 → 100)
    } else {
        push(Math.floor(f.mil / 1000) * 1000, 'moitie_entiere');
        push(Math.ceil(f.mil / 1000) * 1000, 'moitie_entiere');
    }
    return cands.length >= 2 ? shuffle(cands).slice(0, 2) : null;
};

/** Vers la fraction : deux distracteurs en fractions, valeur ≠ clé. */
const distracteursFraction = (f) => {
    const cands = [];
    const push = (n, d, code) => {
        if (n <= 0 || d <= 0 || n * f.d === d * f.n || cands.some((c) => c.n * d === c.d * n)) return;
        cands.push({ n, d, code });
    };
    push(f.d, f.n, 'fraction_inversee');
    if (f.famille === 'dixiemes') { push(f.n, f.d * 10, 'rang'); push(f.n, f.d / 10, 'rang'); }
    else if (f.famille === 'entier') { push(f.n, f.d * 10, 'rang'); push(f.n + f.d, f.d, 'entier_plus_un'); }
    else { push(f.n, 4, 'rang'); push(f.n - 2, 2, 'moitie_voisine'); }
    return cands.length >= 2 ? shuffle(cands).slice(0, 2) : null;
};

const EXPLICATIONS = {
    lu_tel_quel: 'Une fraction n\'est pas une virgule : 7 sur 2, c\'est 7 divisé par 2.',
    rang: 'Un dixième s\'écrit 0,1 et un centième 0,01 : attention au rang du chiffre.',
    entier_lu_dixieme: 'Quand le numérateur et le dénominateur sont égaux, la fraction vaut 1 ; 7 sur 1, c\'est 7.',
    moitie_entiere: 'La moitié d\'un nombre impair n\'est pas un entier : il reste une demi-unité.',
    fraction_inversee: 'Le numérateur est en haut : 3 dixièmes, c\'est 3 sur 10.',
    entier_plus_un: 'Le numérateur doit être exactement un multiple du dénominateur.',
    moitie_voisine: 'Deux moitiés de suite diffèrent de 1 : ce n\'est pas la bonne.',
    regle: 'Une fraction, c\'est une division : le numérateur divisé par le dénominateur.',
};

// ─── Carte 2, format A : série flash (4 trous saisie) ─────────────────────

const genEntiersTrous = () => {
    for (let essai = 0; essai < 80; essai++) {
        // Quatre lignes (six fractions empilées, c'est trop : revue prof
        // 2026-09-07) : une par famille, plus une quatrième tirée.
        const familles = shuffle(['entier', 'dixiemes', 'moitie', randomChoice(['entier', 'dixiemes', 'moitie'])]);
        const fractions = familles.map(tirerFraction);
        if (new Set(fractions.map(cleFraction)).size < 4 || new Set(fractions.map((f) => f.mil)).size < 4) continue;
        // Trous : décimal (majoritaire) ou numérateur ; jamais le dénominateur
        // d'une fraction égale à un entier (7/… = 7 : plusieurs réponses).
        const items = fractions.map((f) => ({
            ...f,
            trou: randInt(1, 3) === 1 ? 'num' : 'dec',
            sens: randInt(0, 1),
        }));
        if (!items.some((it) => it.trou === 'dec') || !items.some((it) => it.trou === 'num')) continue;
        const phrase = items.map((it) => ligneTrou(it, it.trou, it.sens)).join('\n');
        const payload = makeTexteTrou(phrase, '<p><b>Complète chaque égalité.</b></p>', { ...opts(L_ENTIERS), mode: 'saisie' });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
        payload._meta.params = { items };
        return payload;
    }
    const items = [
        { ...verifierFraction(7, 1, 'entier'), trou: 'dec', sens: 0 },
        { ...verifierFraction(1, 10, 'dixiemes'), trou: 'dec', sens: 0 },
        { ...verifierFraction(7, 2, 'moitie'), trou: 'dec', sens: 0 },
        { ...verifierFraction(30, 10, 'entier'), trou: 'num', sens: 0 },
    ];
    const phrase = items.map((it) => ligneTrou(it, it.trou, it.sens)).join('\n');
    const payload = makeTexteTrou(phrase, '<p><b>Complète chaque égalité.</b></p>', { ...opts(L_ENTIERS), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 2, format B : QCM dans les deux sens ───────────────────────────

const genEntiersQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const f = tirerFraction();
        const versDec = randomChoice([true, false]);
        let payload;
        let codes;
        if (versDec) {
            const faux = distracteursDecimal(f);
            if (!faux) continue;
            const choix = [f.dec, ...faux.map((x) => fmt(x.mil))];
            if (new Set(choix).size !== 3) continue;
            payload = makeQcm(choix, f.dec, `<p><b>Quelle est l'écriture décimale de ${tex(fracTex(f))} ?</b></p>`, opts(L_ENTIERS), false);
            codes = faux.map(({ code }) => ({ code }));
        } else {
            const faux = distracteursFraction(f);
            if (!faux) continue;
            const choix = [`$${fracTex(f)}$`, ...faux.map((x) => `$\\dfrac{${x.n}}{${x.d}}$`)];
            if (new Set(choix).size !== 3) continue;
            payload = makeQcm(choix, choix[0], `<p><b>Quelle fraction est égale à ${tex(decTex(f.dec))} ?</b></p>`, opts(L_ENTIERS), false);
            codes = faux.map(({ code }) => ({ code }));
        }
        payload.contenu = { ...payload.contenu, sousType: f.famille, explications: EXPLICATIONS };
        payload._meta.params = { ...f, versDec, distracteurs: codes };
        return payload;
    }
    const f = verifierFraction(7, 1, 'entier');
    const payload = makeQcm(['7', '7,1', '0,7'], '7', `<p><b>Quelle est l'écriture décimale de ${tex(fracTex(f))} ?</b></p>`, opts(L_ENTIERS), false);
    payload.contenu = { ...payload.contenu, sousType: 'entier', explications: EXPLICATIONS };
    payload._meta.params = { ...f, versDec: true, distracteurs: [{ code: 'lu_tel_quel' }, { code: 'entier_lu_dixieme' }] };
    return payload;
};

// ─── Carte 2, format C : Vrai ou faux (3 égalités, 1 ou 2 vraies) ─────────

const genEntiersVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const fractions = shuffle(['entier', 'dixiemes', 'moitie']).map(tirerFraction);
        if (new Set(fractions.map(cleFraction)).size < 3) continue;
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        const codes = [];
        let ok = true;
        const lignes = fractions.map((f, i) => {
            const sens = randInt(0, 1);
            let dec = f.dec;
            if (!verites[i]) {
                const faux = distracteursDecimal(f);
                if (!faux) { ok = false; return ''; }
                const d = randomChoice(faux);
                if (d.mil === f.mil) { ok = false; return ''; }
                dec = fmt(d.mil);
                codes.push(d.code);
            }
            return '$' + (sens === 0 ? `${fracTex(f)} = ${decTex(dec)}` : `${decTex(dec)} = ${fracTex(f)}`) + '$';
        });
        if (!ok || new Set(lignes).size < 3) continue;
        const payload = makeVraiFaux(lignes, verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_ENTIERS));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { fractions, verites, distracteurs: codes.map((code) => ({ code })) };
        return payload;
    }
    const fractions = [verifierFraction(7, 1, 'entier'), verifierFraction(7, 10, 'dixiemes'), verifierFraction(9, 2, 'moitie')];
    const lignes = ['$\\dfrac{7}{1} = 7$', '$\\dfrac{7}{10} = 0{,}07$', '$4{,}5 = \\dfrac{9}{2}$'];
    const payload = makeVraiFaux(lignes, [0, 1, 0], '<p><b>Vrai ou faux ?</b></p>', opts(L_ENTIERS));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { fractions, verites: [true, false, true], distracteurs: [{ code: 'rang' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'carte 1 : le contrat de gen_1012 (virgule collée, fraction inversée, autre fait du domaine)',
        'carte 2 : trois choix, chaque distracteur vérifié différent de la clé en VALEUR ; collision → nouveau tirage complet',
        'chaque distracteur porte son code dans _meta.params.distracteurs ; contenu.sousType = la famille (entier, dixiemes, moitie)',
    ],
    entiers_qcm_unique: {
        support: 'une fraction du domaine de la carte 2, dans un sens ou dans l\'autre',
        fixes: {
            lu_tel_quel: 'les chiffres lus tels quels : 1/10 → 1,1 ; 7/2 → 7,2 ; 7/1 → 7,1 (LE piège)',
            rang: 'erreur de rang : 1/100 → 0,1 ; 3/10 → 0,03 ; vers la fraction 0,3 → 3/100',
            entier_lu_dixieme: 'la fraction égale à un entier lue comme un dixième (100/100 → 0,1 ; 7/1 → 0,7) ou comme son numérateur (100)',
            moitie_entiere: 'la moitié arrondie : 7/2 → 3 ou 4',
            fraction_inversee: 'vers la fraction : 0,3 → 10/3',
            entier_plus_un: 'vers la fraction : 7 → 8/1',
            moitie_voisine: 'vers la fraction : 3,5 → 5/2',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    entiers_texte_trou_saisie: genEntiersTrous,
    entiers_qcm_unique: genEntiersQcm,
    entiers_tableau_vf: genEntiersVf,
};

export const CATEGORIES = [
    ...CAT_USUELLES,
    { id: 'entiers', label: L_ENTIERS, formatsList: [
        { key: 'entiers_texte_trou_saisie', label: 'Saisie trous' },
        { key: 'entiers_qcm_unique', label: 'QCM' },
        { key: 'entiers_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    if (typeExercice && typeExercice.startsWith('usuelles')) return rebadger(genererUsuelles(typeExercice));
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        if (!matching.length) return rebadger(genererUsuelles(typeExercice));
        key = randomChoice(matching);
    } else if (randomChoice([true, false])) {
        return rebadger(genererUsuelles());
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
